import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useValueAssessmentTelemetry } from '@/hooks/useValueAssessmentTelemetry';
import { saveCareerContextFromExtraction, type ExtractedCareerContext } from '@/lib/save-career-context';

const REFLECTION_PROMPTS = [
  {
    question: "Think of a time when you felt truly fulfilled and proud. What were you doing, and what made it meaningful?",
    hint: "Consider both professional and personal moments",
  },
  {
    question: "What frustrates you most when you see it in the workplace or world? What value is being violated?",
    hint: "Frustration often points to deeply held values",
  },
  {
    question: "Who do you admire most, and what qualities do they embody that you aspire to?",
    hint: "Role models reflect our aspirational values",
  },
  {
    question: "If you had unlimited resources and no fear of failure, what would you spend your time doing?",
    hint: "This reveals intrinsic motivations",
  },
  {
    question: "What would you want people to say about you at the end of your career?",
    hint: "Legacy thinking clarifies core priorities",
  },
];

interface ReflectionPromptsProps {
  onComplete: (values: { name: string; description: string; importance: number }[]) => void;
  onCancel: () => void;
  initialDraftData?: Record<string, unknown>;
  onSaveDraft?: (data: Record<string, unknown>) => void;
  assessmentId?: string | null;
}

export const ReflectionPrompts = ({ onComplete, onCancel, initialDraftData, onSaveDraft, assessmentId }: ReflectionPromptsProps) => {
  const { user } = useAuth();
  const { profile, invalidate: invalidateProfile } = useProfile();
  const [currentPrompt, setCurrentPrompt] = useState(() => {
    if (initialDraftData?.currentPrompt != null) return initialDraftData.currentPrompt as number;
    return 0;
  });
  const [answers, setAnswers] = useState<string[]>(() => {
    if (initialDraftData?.answers) return initialDraftData.answers as string[];
    return Array(REFLECTION_PROMPTS.length).fill('');
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const {
    startAssessment,
    restoreSession,
    updateProgress,
    completeAssessment,
    abandonAssessment
  } = useValueAssessmentTelemetry();

  // Start or restore tracking when component mounts
  useEffect(() => {
    if (initialDraftData && assessmentId) {
      restoreSession(assessmentId, 'reflection_prompts');
    } else {
      startAssessment('reflection_prompts', 'dashboard');
    }
  }, []);

  const progress = ((currentPrompt + 1) / REFLECTION_PROMPTS.length) * 100;
  const canProceed = answers[currentPrompt].trim().length > 20;
  const allAnswered = answers.every(a => a.trim().length > 20);

  const handleNext = () => {
    if (currentPrompt < REFLECTION_PROMPTS.length - 1) {
      const nextPrompt = currentPrompt + 1;
      setCurrentPrompt(nextPrompt);
      updateProgress(nextPrompt + 1, {
        reflection_responses: answers.slice(0, nextPrompt + 1).map((a, i) => ({
          prompt: REFLECTION_PROMPTS[i].question,
          answer_length: a.length,
        })),
      });
      onSaveDraft?.({ schema_version: 1, answers, currentPrompt: nextPrompt });
    }
  };

  const handlePrev = () => {
    if (currentPrompt > 0) {
      setCurrentPrompt(prev => prev - 1);
    }
  };

  const handleCancel = () => {
    abandonAssessment('user_cancelled');
    onCancel();
  };

  const analyzeAndExtract = async () => {
    setIsAnalyzing(true);
    try {
      // Get current session for authenticated request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Build a prompt from all answers
      const reflectionText = REFLECTION_PROMPTS.map((p, i) => 
        `Q: ${p.question}\nA: ${answers[i]}`
      ).join('\n\n');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/discover-values`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: [
              { role: 'user', content: `Here are my reflections on some value-discovery questions:\n\n${reflectionText}` }
            ],
            action: 'extract',
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 429) {
          const errorData = await response.json().catch(() => ({}));
          const seconds = errorData.retry_after_seconds;
          const timeMsg = seconds >= 60 ? `${Math.ceil(seconds / 60)} minute(s)` : `${seconds || 60} seconds`;
          throw new Error(`Too many requests. Please try again in ${timeMsg}.`);
        }
        throw new Error('Failed to analyze');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response');

      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) fullContent += content;
          } catch {
            // Skip
          }
        }
      }

      const jsonMatch = fullContent.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('Could not parse values');

      const extracted = JSON.parse(jsonMatch[0]);
      const values = (extracted.values || []).map((v: any, i: number) => ({
        name: v.name,
        description: v.description,
        importance: i + 1,
      }));

      // Save career context from extraction (fire-and-forget)
      const careerCtx: ExtractedCareerContext | undefined = extracted.career_context;
      if (careerCtx && user) {
        const saved = await saveCareerContextFromExtraction(user.id, profile, careerCtx);
        if (saved) await invalidateProfile();
      }

      if (values.length > 0) {
        completeAssessment(
          values.map((v: { name: string }) => v.name),
          {
            reflection_responses: answers.map((a, i) => ({
              prompt: REFLECTION_PROMPTS[i].question,
              answer_length: a.length,
            })),
          }
        );
        onComplete(values);
      } else {
        throw new Error('No values extracted');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      // Fallback: create generic values based on keywords
      const fallbackValues = [
        { name: 'Growth', description: 'Based on your reflections about personal development', importance: 1 },
        { name: 'Impact', description: 'Your desire to make a meaningful difference', importance: 2 },
        { name: 'Authenticity', description: 'Being true to yourself and your beliefs', importance: 3 },
      ];
      completeAssessment(
        fallbackValues.map(v => v.name),
        { reflection_responses: answers.map((a, i) => ({ prompt: REFLECTION_PROMPTS[i].question, answer_length: a.length })) }
      );
      onComplete(fallbackValues);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <Badge variant="outline">
          Question {currentPrompt + 1} of {REFLECTION_PROMPTS.length}
        </Badge>
        <Progress value={progress} className="w-24 h-2" />
      </div>

      <div className="space-y-3">
        <h3 className="font-medium text-lg leading-snug">
          {REFLECTION_PROMPTS[currentPrompt].question}
        </h3>
        <p className="text-xs text-muted-foreground italic">
          {REFLECTION_PROMPTS[currentPrompt].hint}
        </p>
        <Textarea
          value={answers[currentPrompt]}
          onChange={(e) => {
            const newAnswers = [...answers];
            newAnswers[currentPrompt] = e.target.value;
            setAnswers(newAnswers);
            onSaveDraft?.({ schema_version: 1, answers: newAnswers, currentPrompt });
          }}
          placeholder="Take your time to reflect and write your thoughts..."
          className="min-h-[120px] resize-none"
        />
        {answers[currentPrompt].trim().length > 0 && answers[currentPrompt].trim().length < 20 && (
          <p className="text-xs text-amber-600">
            Please write a bit more for a meaningful reflection
          </p>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        {currentPrompt > 0 && (
          <Button variant="outline" onClick={handlePrev} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Previous
          </Button>
        )}
        {currentPrompt < REFLECTION_PROMPTS.length - 1 ? (
          <Button onClick={handleNext} disabled={!canProceed} className="flex-1 gap-2">
            Next Question
            <ArrowRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button 
            onClick={analyzeAndExtract} 
            disabled={!allAnswered || isAnalyzing}
            className="flex-1 gap-2"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing your reflections...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Discover My Values
              </>
            )}
          </Button>
        )}
      </div>

      <Button variant="ghost" onClick={handleCancel} className="w-full">
        Cancel
      </Button>
    </div>
  );
};
